import { Helmet } from "react-helmet-async";
import { MultiViewSlider, Pagination } from "reusableComponents";
import { useTranslation } from "react-i18next";
import { useEffect, useState, useRef } from "react";
import { useLanguage } from "context/languageContext";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getOtherFilterOptions } from "services/filters.services";
import { getSpacesByFilters } from "services/spaceList.services";
import { setFilters } from "store/filters/filtersSlice";
import {
  updateURLWithChangedFilters,
  getFiltersFromQueryParams,
} from "helpers/searchParams";
import { Filters, SpacesList } from "./components";
import styles from "./styles.module.scss";
import { useLocation } from "react-router-dom";
import SpaceCardStyled from "reusableComponents/SpaceCardStyled";
import SpaceCardStyledSkeleton from "reusableComponents/skeletons/SpaceCardStyledSkeleton";

const SpaceListPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const token = useSelector((state) => state.auth.token);
  const { language } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [spaces, setSpaces] = useState([]);
  const [memberPlusSpaces, setMemberPlusSpaces] = useState([]);
  const [moreFilterOptions, setMoreFilterOptions] = useState([]);
  const [prevFiltersStored, setPrevFiltersStored] = useState({});
  const [pagination, setPagination] = useState({
    total: 0,
    per_page: 16,
    current_page: 1,
    next_page_url: null,
    prev_page_url: null,
    has_more_pages: true,
  });
  const [activeSpace, setActiveSpace] = useState(null);

  const handleSelectSpace = (space) => {
    setActiveSpace(space);
  };

  const handleDeselectSpace = () => {
    // setActiveSpace(null);
  };
  const { t } = useTranslation("global");
  const filters = useSelector((state) => state.filters);
  const prevFiltersRef = useRef(filters);
  const prevPageRef = useRef(pagination.current_page);
  const [filtersInitialized, setFiltersInitialized] = useState(false);
  const [filterHistory, setFilterHistory] = useState([filters]);
  const isFirstRender = useRef(true);

  useEffect(() => {
    const lastFilters = filterHistory[filterHistory.length - 1];

    // Deep comparison to prevent duplicates
    if (JSON.stringify(filters) !== JSON.stringify(lastFilters)) {
      setFilterHistory((prevHistory) => {
        const newHistory = [...prevHistory, filters];
        if (newHistory.length > 7) {
          newHistory.shift();
        }
        return newHistory;
      });
    }
  }, [filters]);

  const handleResetFilters = () => {
    if (filterHistory.length > 1) {
      const previousFilters = filterHistory[filterHistory.length - 2];
      dispatch(setFilters(previousFilters));
      setFilterHistory((prevHistory) => prevHistory.slice(0, -1));
    } else {
      console.log("No previous filters to reset to.");
    }
  };

  // Update filters from URL query params on initial load (only once)
  useEffect(() => {
    if (location.search) {
      const queryParams = new URLSearchParams(location.search);
      const filtersFromQuery = getFiltersFromQueryParams(queryParams.toString());
      if (Object.keys(filtersFromQuery).length > 0) {
        dispatch(setFilters(filtersFromQuery));
      }
    }
    setFiltersInitialized(true);
  }, []); // Empty dependency array - only run once on mount

  // Fetch spaces based on filters and pagination
  useEffect(() => {
    const fetchSpaces = async () => {
      setActiveSpace(null);
      setLoading(true);
      setSpaces([]);
      setError(null);
      try {
        const page =
          JSON.stringify(filters) !== JSON.stringify(prevFiltersRef.current)
            ? 1
            : pagination.current_page;
        const response = await getSpacesByFilters({
          ...filters,
          member_plus: "excluded",
          page,
          perPage: pagination.per_page,
          lang: language,
          token,
        });

        const memberPlusResponse = await getSpacesByFilters({
          ...filters,
          member_plus: "only",
          page,
          perPage: 10,
          lang: language,
          token,
        });

        setMemberPlusSpaces(memberPlusResponse.data);
        setSpaces(response.data);
        setPagination((prevState) => ({
          total: response.pagination.total,
          per_page: 16,
          current_page: response.pagination.current_page,
          has_more_pages: response.pagination.has_more_pages,
          next_page_url: response.pagination.next_page_url,
          prev_page_url: response.pagination.prev_page_url,
        }));
      } catch (err) {
        setError(err.message || "Failed to fetch spaces. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    const fetchFilterOptions = async () => {
      try {
        const options = await getOtherFilterOptions({
          ...filters,
          lang: language,
        });
        setMoreFilterOptions(options);
      } catch (err) {
        console.error(err);
      }
    };

    if (filtersInitialized) {
      setSpaces([]);
      fetchSpaces();
      fetchFilterOptions();
      
      // Only update URL if this is not the first render after restoring from URL
      if (!isFirstRender.current) {
        updateURLWithChangedFilters(filters);
      } else {
        isFirstRender.current = false;
      }
      
      setPrevFiltersStored(prevFiltersRef.current);
      prevFiltersRef.current = filters;
      prevPageRef.current = pagination.current_page;
    }

    // Scroll to top after fetch
    setTimeout(() => {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }, 100);
  }, [
    filters,
    pagination.per_page,
    pagination.current_page,
    language,
    filtersInitialized,
  ]);

  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage !== pagination.current_page) {
      setPagination((prevState) => ({
        ...prevState,
        current_page: newPage,
      }));
    }
  };

  if (error) {
    navigate("/notfound");
  }

  // SEO Meta Data
  const currentLanguage = language || 'fr';
  const metaTitle = t("searchesList.metaTitle");
  const metaDescription = t("searchesList.metaDescription");
  const metaKeywords = t("searchesList.metaKeywords");
  
  // Get current canonical URL
  const currentPath = window.location.pathname;
  const baseUrl = "https://chooseandwork.com";
  const canonicalUrl = `${baseUrl}${currentPath}${location.search}`;
  
  // Open Graph image
  const ogImage = "https://chooseandwork.com/og-image-search.jpg";
  const twitterImage = "https://chooseandwork.com/twitter-image-search.jpg";
  
  // Determine if paginated page (noindex for pages > 1)
  const isPaginated = pagination.current_page > 1;

  return (
    <>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        <meta name="keywords" content={metaKeywords} />
        <link rel="canonical" href={canonicalUrl} />
        
        {/* Open Graph Tags */}
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={ogImage} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:locale" content={currentLanguage === 'fr' ? 'fr_FR' : currentLanguage === 'en' ? 'en_US' : 'es_ES'} />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={metaTitle} />
        <meta name="twitter:description" content={metaDescription} />
        <meta name="twitter:image" content={twitterImage} />
        
        {/* Additional meta tags - noindex for paginated pages */}
        {isPaginated ? (
          <meta name="robots" content="noindex, follow" />
        ) : (
          <meta name="robots" content="index, follow" />
        )}
        
        {/* Structured Data for Search Results Page */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SearchResultsPage",
            "name": metaTitle,
            "url": canonicalUrl,
            "description": metaDescription,
            "mainEntity": {
              "@type": "ItemList",
              "numberOfItems": pagination.total,
              "itemListOrder": "https://schema.org/ItemListOrderDescending"
            }
          })}
        </script>
      </Helmet>

      <section className={styles.spacesListPage}>
        <Filters
          moreFilterOptions={moreFilterOptions}
          spaces={spaces}
          activeSpace={activeSpace}
          setActiveSpace={handleSelectSpace}
        />
        <div className={`${styles.bodyWrapper} pageContainer`}>
          <div className={styles.pageTitle}>
            <div>
              <a href="/">{t("home.title")}</a> /<span>{t("search.title")}</span>
            </div>
          </div>
         {(memberPlusSpaces.length > 0 || loading) && <>
          <p className={styles.memberPlusSpacesTitle}>{t("search.memberPlusSpaces")}</p>
          <MultiViewSlider
            autoplay={true}
            infinite={true}
            key={`992-${memberPlusSpaces.length}`}
            arrowLeft="-44px"
            arrowRight="-44px"
            slidesNum={5}
            arrowStyle="square"
           responsive={[
  {
    breakpoint: 1725,
    settings: {
      slidesToShow: 4,
      slidesToScroll: 4,
    },
  },
  {
    breakpoint: 1500,
    settings: {
      slidesToShow: 3,
      slidesToScroll: 3,
    },
  },
  {
    breakpoint: 992,
    settings: {
      slidesToShow: 2,
      slidesToScroll: 2,
    },
  },
  {
    breakpoint: 768,
    settings: {
      slidesToShow: 1,
      slidesToScroll: 1,
    },
  },
]}
          >
            {loading ? (
              Array.from({ length: 5 }).map((_, index) => (
                <div className={styles.memberPlusSpaceCardContainer} key={`skeleton-${index}`}>
                  <SpaceCardStyledSkeleton />
                </div>
              ))
            ) : (
              memberPlusSpaces.map((space) => (
                <div className={styles.memberPlusSpaceCardContainer} key={space.id}>
                  <SpaceCardStyled place={space} />
                </div>
              ))
            )}
          </MultiViewSlider></> }

          <span className={styles.totalAmount}>
            {t("search.total")}
            {pagination.total}
          </span>
          <SpacesList
            loading={loading}
            error={error}
            spaces={spaces}
            pagination={pagination}
            onHoverSpace={handleSelectSpace}
            onLeaveSpace={handleDeselectSpace}
            resetPrevFilters={handleResetFilters}
            showResetButton={filterHistory.length > 2}
          />

          {!loading && pagination.total > 1 && (
            <Pagination
              itemsPerPage={16}
              totalItems={pagination.total}
              currentPage={pagination.current_page}
              onPageChange={handlePageChange}
              hasMorePages={pagination.has_more_pages}
            />
          )}
        </div>
      </section>
    </>
  );
};

export default SpaceListPage;